import { useState, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { FaDiscord } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { handleLogin, handleOAuthSignIn, handleSignUp } from '@components'
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '@client/store';
import { usePyrenzAlert } from '@renderer/provider';
import {
  Box,
  Modal,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Backdrop,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PyrenzBlueButton } from '@client/theme';

interface AuthenticationModalProps {
  mode: 'login' | 'register';
  onClose: () => void;
  toggleMode: () => void;
}

export function AuthenticationModal({
  mode,
  onClose,
  toggleMode: toggleModeProp,
}: AuthenticationModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isAdult, setIsAdult] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const { t } = useTranslation();
  const setIsLogin = useUserStore((state) => state.setIsLogin);
  const showAlert = usePyrenzAlert();
  const navigate = useNavigate();

  const resetCaptcha = () => {
    setCaptchaToken(null);
    if (captchaRef.current) {
      captchaRef.current.resetCaptcha();
    }
  };

  const toggleMode = () => {
    resetCaptcha();
    setUsername('');
    toggleModeProp();
  };

  const getErrorMessage = (err: any): string => {
    if (err?.error && typeof err.error === 'string') {
      return err.error;
    }
    if (err?.message && typeof err.message === 'string') {
      return err.message;
    }
    return 'An unexpected error occurred.';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaToken) {
      showAlert('Please complete the CAPTCHA.', 'Error');
      return;
    }

    if (mode === 'register' && !isAdult) {
      showAlert('You must be 18 or older to sign up.', 'Error');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        const result = await handleLogin(email, password, captchaToken);
        if (result.success) {
          showAlert('Login successful! Welcome back. ₍ᐢ. .ᐢ₎', 'Success');
          setIsLogin(true);
          onClose();
        }
      } else {
        const result = await handleSignUp(email, password, captchaToken, username || undefined);
        if (result.success) {
          showAlert('Signed up successfully! Welcome aboard. ₍ᐢ. .ᐢ₎', 'Success');
          setIsLogin(true);
          onClose();
        }
      }
    } catch (err: any) {
      const errorMessage = getErrorMessage(err);
      showAlert(errorMessage, 'Error');
      Sentry.captureException(err);
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'discord') => {
    setLoading(true);
    try {
      const result = await handleOAuthSignIn(provider);
      if (result.success) {
        showAlert(`Signed in with ${provider.charAt(0).toUpperCase() + provider.slice(1)} successfully!`, 'Success');
        setIsLogin(true);
        onClose();
      }
    } catch (err: any) {
      const errorMessage = getErrorMessage(err);
      showAlert(errorMessage, 'Error');
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const onCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  return (
    <Modal
      open
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          outline: 'none',
        }}
      >
        <Box
          sx={{
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(30, 30, 47, 0.7)',
            borderRadius: '16px',
            p: 4,
            width: 400,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.37)',
            color: 'white',
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', fontWeight: 700 }}>
            {mode === 'login' ? t('buttons.login') : t('buttons.createAccount')}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={6}>
              <PyrenzBlueButton
                variant="outlined"
                startIcon={<FcGoogle />}
                onClick={() => handleOAuth('google')}
                disabled={loading}
                fullWidth
                sx={{
                  color: 'white',
                  borderColor: '#555',
                  '&:hover': { borderColor: '#888' },
                }}
              >
                Google
              </PyrenzBlueButton>
            </Grid>
            <Grid size={6}>
              <PyrenzBlueButton
                variant="outlined"
                startIcon={<FaDiscord style={{ color: 'white' }} />}
                onClick={() => handleOAuth('discord')}
                disabled={loading}
                fullWidth
                sx={{
                  color: 'white',
                  borderColor: '#5865F2',
                  backgroundColor: '#5865F2',
                  '&:hover': { backgroundColor: '#4752c4' },
                }}
              >
                Discord
              </PyrenzBlueButton>
            </Grid>
          </Grid>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {mode === 'register' && (
              <TextField
                label={t('auth.username') || 'Username'}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                InputLabelProps={{ style: { color: '#ccc' } }}
                InputProps={{ style: { color: '#fff' } }}
              />
            )}
            <TextField
              label={t('auth.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              InputLabelProps={{ style: { color: '#ccc' } }}
              InputProps={{ style: { color: '#fff' } }}
            />
            <TextField
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              InputLabelProps={{ style: { color: '#ccc' } }}
              InputProps={{ style: { color: '#fff' } }}
            />
            {mode === 'register' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAdult}
                    onChange={() => setIsAdult(!isAdult)}
                    sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                  />
                }
                label={<Typography variant="body2">{t('auth.confirm18')}</Typography>}
                sx={{ color: '#fff' }}
              />
            )}
            <PyrenzBlueButton
              type="submit"
              variant="contained"
              disabled={loading || !captchaToken || (mode === 'register' && !isAdult)}
              sx={{
                backgroundColor: '#3f51b5',
                '&:hover': { backgroundColor: '#334296' },
              }}
            >
              {loading
                ? `${mode === 'login' ? t('auth.loggingIn') : t('auth.signingUp')}...`
                : mode === 'login'
                  ? t('buttons.login')
                  : t('buttons.signUp')}
            </PyrenzBlueButton>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <HCaptcha
                sitekey="3310c5ee-9cec-4026-a2ba-02ca73d24f9c"
                onVerify={onCaptchaChange}
                ref={captchaRef}
              />
            </Box>
          </Box>

          <Grid container spacing={1} sx={{ mt: 2 }}>
            <Grid size={12}>
              <Typography variant="body2" align="center" sx={{ color: '#aaa' }}>
                By continuing, you agree to our{' '}
                <Typography
                  component="span"
                  variant="body2"
                  onClick={() => navigate('/Policy')}
                  sx={{ color: '#3f51b5', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Privacy Policy
                </Typography>
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body2" align="center" sx={{ color: '#aaa' }}>
                {mode === 'login' ? t('auth.dontHaveAccount') : t('auth.alreadyHaveAccount')}{' '}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ color: '#3f51b5', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={toggleMode}
                >
                  {mode === 'login' ? t('buttons.register') : t('buttons.login')}
                </Typography>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
}